import { useState, useEffect, useCallback } from 'react'
import {
  WorkOrderTypes,
  WorkOrderStatuses,
  Priorities,
  ProductCategoryLabels,
  type WorkOrder,
  type WorkOrderInput,
  type Product,
} from '@esf/shared'
import { getApiBase } from '../utils/api'

interface WorkOrderFormPageProps {
  workOrderId?: string | null
  preselectedAssetId?: string | null
  onComplete: () => void
  onCancel: () => void
}

const workOrderTypeLabels: Record<string, string> = {
  INSPECTION: 'Inspection',
  MAINTENANCE: 'Scheduled Maintenance',
  REPAIR: 'Repair',
  INSTALLATION: 'Installation',
  DECOMMISSION: 'Decommission',
  SENSOR_REPLACEMENT: 'Sensor Replacement',
}

const workOrderTypeDescriptions: Record<string, string> = {
  INSPECTION: 'Routine check of asset condition and functionality',
  MAINTENANCE: 'Planned preventive maintenance activities',
  REPAIR: 'Fix identified issues or damage',
  INSTALLATION: 'New asset installation or setup',
  DECOMMISSION: 'Remove asset from service',
  SENSOR_REPLACEMENT: 'Replace IoT sensors or monitoring equipment',
}

const priorityConfig: Record<string, { color: string; bg: string; description: string }> = {
  LOW: { color: 'text-gray-700', bg: 'bg-gray-100', description: 'Can be scheduled flexibly' },
  MEDIUM: { color: 'text-blue-700', bg: 'bg-blue-100', description: 'Standard priority, schedule within normal timeframe' },
  HIGH: { color: 'text-amber-700', bg: 'bg-amber-100', description: 'Requires prompt attention' },
  URGENT: { color: 'text-red-700', bg: 'bg-red-100', description: 'Immediate action required' },
}

export default function WorkOrderFormPage({
  workOrderId,
  preselectedAssetId,
  onComplete,
  onCancel,
}: WorkOrderFormPageProps) {
  const base = getApiBase()
  const defaultHeaders = { 'Content-Type': 'application/json' }

  const [formData, setFormData] = useState<WorkOrderInput>({
    assetId: preselectedAssetId || '',
    type: 'INSPECTION',
    priority: 'MEDIUM',
    title: '',
    description: '',
    assignedTo: '',
    scheduledDate: '',
  })
  const [workOrder, setWorkOrder] = useState<WorkOrder | null>(null)
  const [products, setProducts] = useState<Product[]>([])
  const [assignableUsers, setAssignableUsers] = useState<{ email: string; name: string; role: string }[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [assetSearch, setAssetSearch] = useState('')
  const [showAssetSelector, setShowAssetSelector] = useState(!preselectedAssetId)

  const isEditing = !!workOrderId

  // Fetch products for asset selector
  const fetchProducts = useCallback(async () => {
    try {
      const res = await fetch(`${base}/assets`, {
        headers: defaultHeaders,
        credentials: 'include',
      })
      if (res.ok) {
        const data = await res.json()
        setProducts(data.items || [])
      }
    } catch {
      // Silently fail - products will just be empty
    }
  }, [base])

  // Fetch assignable users for dropdown
  const fetchAssignableUsers = useCallback(async () => {
    try {
      const res = await fetch(`${base}/users/assignable`, {
        headers: defaultHeaders,
        credentials: 'include',
      })
      if (res.ok) {
        const data = await res.json()
        setAssignableUsers(data.items || [])
      }
    } catch {
      // Silently fail - users will just be empty
    }
  }, [base])

  // Fetch work order if editing
  const fetchWorkOrder = useCallback(async () => {
    if (!workOrderId) return
    setIsFetching(true)
    try {
      const res = await fetch(`${base}/workorders/${workOrderId}`, {
        headers: defaultHeaders,
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to fetch work order')
      const data = await res.json()
      setWorkOrder(data)
    } catch {
      setErrors({ fetch: 'Failed to load work order' })
    } finally {
      setIsFetching(false)
    }
  }, [workOrderId, base])

  useEffect(() => {
    fetchProducts()
    fetchAssignableUsers()
    if (workOrderId) {
      fetchWorkOrder()
    }
  }, [fetchProducts, fetchAssignableUsers, workOrderId, fetchWorkOrder])

  // Populate form when work order is loaded
  useEffect(() => {
    if (workOrder) {
      setFormData({
        assetId: workOrder.assetId,
        type: workOrder.type,
        priority: workOrder.priority,
        title: workOrder.title,
        description: workOrder.description || '',
        assignedTo: workOrder.assignedTo || '',
        scheduledDate: workOrder.scheduledDate?.split('T')[0] || '',
      })
      setShowAssetSelector(false)
    }
  }, [workOrder])

  // Set preselected asset
  useEffect(() => {
    if (preselectedAssetId && !workOrderId) {
      setFormData(prev => ({ ...prev, assetId: preselectedAssetId }))
      setShowAssetSelector(false)
    }
  }, [preselectedAssetId, workOrderId])

  const handleChange = (field: keyof WorkOrderInput, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!formData.assetId) {
      newErrors.assetId = 'Please select an asset'
    }
    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required'
    }
    if (!formData.type) {
      newErrors.type = 'Type is required'
    }
    if (!formData.priority) {
      newErrors.priority = 'Priority is required'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    const cleanData: WorkOrderInput = {
      ...formData,
      scheduledDate: formData.scheduledDate || undefined,
      description: formData.description || undefined,
      assignedTo: formData.assignedTo || undefined,
    }

    setIsLoading(true)
    try {
      const url = isEditing ? `${base}/workorders/${workOrderId}` : `${base}/workorders`
      const method = isEditing ? 'PATCH' : 'POST'

      const res = await fetch(url, {
        method,
        headers: defaultHeaders,
        credentials: 'include',
        body: JSON.stringify(cleanData),
      })

      if (!res.ok) throw new Error(`Failed to ${isEditing ? 'update' : 'create'} work order`)
      onComplete()
    } catch {
      setErrors({ submit: `Failed to ${isEditing ? 'update' : 'create'} work order. Please try again.` })
    } finally {
      setIsLoading(false)
    }
  }

  const filteredProducts = products.filter(p =>
    !assetSearch ||
    p.name.toLowerCase().includes(assetSearch.toLowerCase()) ||
    p.passportId.toLowerCase().includes(assetSearch.toLowerCase()) ||
    p.installationSite?.toLowerCase().includes(assetSearch.toLowerCase())
  )

  const selectedProduct = products.find(p => p.id === formData.assetId)

  const inputClass = (field: string) =>
    `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-esf-navy/20 focus:border-esf-navy transition-colors ${
      errors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300'
    }`

  if (isFetching) {
    return (
      <div className="container-page">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-gray-500">
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Loading...
          </div>
        </div>
      </div>
    )
  }

  if (errors.fetch) {
    return (
      <div className="container-page">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <p className="text-red-600">{errors.fetch}</p>
          <button onClick={onCancel} className="btn btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container-page">
      {/* Page Header */}
      <div className="mb-6">
        <button
          onClick={onCancel}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Work Orders
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Edit Work Order' : 'Create Work Order'}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          {isEditing
            ? 'Update work order details and assignment'
            : 'Schedule maintenance, repairs, or inspections for an asset'}
        </p>
      </div>

      {/* Error Banner */}
      {errors.submit && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Asset Selection Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-semibold text-gray-900">Asset</h2>
                <p className="text-sm text-gray-500">Select the asset this work order is for</p>
              </div>
              <div className="p-6">
                {selectedProduct && !showAssetSelector ? (
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{selectedProduct.name}</div>
                        <div className="text-sm text-gray-500">
                          <span className="font-mono">{selectedProduct.passportId}</span>
                          {selectedProduct.installationSite && (
                            <span className="ml-2">• {selectedProduct.installationSite}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {ProductCategoryLabels[selectedProduct.category as keyof typeof ProductCategoryLabels]}
                          </span>
                        </div>
                      </div>
                    </div>
                    {!preselectedAssetId && (
                      <button
                        type="button"
                        onClick={() => setShowAssetSelector(true)}
                        className="btn btn-secondary btn-sm"
                      >
                        Change
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Search Input */}
                    <div className="relative">
                      <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search by name, passport ID, or location..."
                        value={assetSearch}
                        onChange={e => setAssetSearch(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-esf-navy/20 focus:border-esf-navy"
                      />
                    </div>

                    {/* Asset List */}
                    <div className={`max-h-64 overflow-y-auto border rounded-lg divide-y ${errors.assetId ? 'border-red-500' : 'border-gray-200'}`}>
                      {filteredProducts.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p>No assets found</p>
                          {assetSearch && (
                            <button
                              type="button"
                              onClick={() => setAssetSearch('')}
                              className="text-blue-600 hover:underline mt-1"
                            >
                              Clear search
                            </button>
                          )}
                        </div>
                      ) : (
                        filteredProducts.slice(0, 15).map(p => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => {
                              handleChange('assetId', p.id)
                              setAssetSearch('')
                              setShowAssetSelector(false)
                            }}
                            className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-gray-900">{p.name}</div>
                                <div className="text-sm text-gray-500">
                                  <span className="font-mono">{p.passportId}</span>
                                  {p.installationSite && (
                                    <span className="ml-2">• {p.installationSite}</span>
                                  )}
                                </div>
                              </div>
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {ProductCategoryLabels[p.category as keyof typeof ProductCategoryLabels]}
                              </span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                    {errors.assetId && <p className="text-red-500 text-sm">{errors.assetId}</p>}
                  </div>
                )}
              </div>
            </div>

            {/* Work Order Details Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-semibold text-gray-900">Work Order Details</h2>
                <p className="text-sm text-gray-500">Describe the work to be performed</p>
              </div>
              <div className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => handleChange('title', e.target.value)}
                    className={inputClass('title')}
                    placeholder="Brief summary of the work (e.g., 'Annual safety inspection')"
                  />
                  {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={e => handleChange('description', e.target.value)}
                    className={`${inputClass('description')} min-h-[160px] resize-y`}
                    placeholder="Provide detailed instructions for the technician...

• What needs to be done?
• Any specific tools or parts required?
• Safety considerations?
• Expected outcomes?"
                    rows={6}
                  />
                </div>
              </div>
            </div>

            {/* Assignment & Scheduling Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-semibold text-gray-900">Assignment & Scheduling</h2>
                <p className="text-sm text-gray-500">Assign the work and set a target date</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assigned To
                    </label>
                    <select
                      value={formData.assignedTo || ''}
                      onChange={e => handleChange('assignedTo', e.target.value)}
                      className={inputClass('assignedTo')}
                    >
                      <option value="">Select a user...</option>
                      {assignableUsers.map(user => (
                        <option key={user.email} value={user.email}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select the responsible technician</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Scheduled Date
                    </label>
                    <input
                      type="date"
                      value={formData.scheduledDate || ''}
                      onChange={e => handleChange('scheduledDate', e.target.value)}
                      className={inputClass('scheduledDate')}
                    />
                    <p className="text-xs text-gray-500 mt-1">Target completion date</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-6">
            {/* Type Selection Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-semibold text-gray-900">Work Type</h2>
              </div>
              <div className="p-4 space-y-2">
                {WorkOrderTypes.map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => handleChange('type', t)}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      formData.type === t
                        ? 'border-esf-navy bg-esf-navy/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.type === t ? 'border-esf-navy' : 'border-gray-300'
                      }`}>
                        {formData.type === t && (
                          <div className="w-2 h-2 rounded-full bg-esf-navy" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{workOrderTypeLabels[t]}</div>
                        <div className="text-xs text-gray-500">{workOrderTypeDescriptions[t]}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Priority Selection Card */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="font-semibold text-gray-900">Priority</h2>
              </div>
              <div className="p-4 space-y-2">
                {Priorities.map(p => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleChange('priority', p)}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      formData.priority === p
                        ? `border-current ${priorityConfig[p].bg}`
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        formData.priority === p ? priorityConfig[p].color : 'border-gray-300'
                      }`}>
                        {formData.priority === p && (
                          <div className={`w-2 h-2 rounded-full ${priorityConfig[p].bg.replace('100', '500')}`} />
                        )}
                      </div>
                      <div>
                        <div className={`font-medium ${formData.priority === p ? priorityConfig[p].color : 'text-gray-900'}`}>
                          {p}
                        </div>
                        <div className="text-xs text-gray-500">{priorityConfig[p].description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Status (only when editing) */}
            {workOrder && (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <h2 className="font-semibold text-gray-900">Current Status</h2>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {WorkOrderStatuses.map(s => (
                      <span
                        key={s}
                        className={`badge ${
                          workOrder.status === s ? 'bg-esf-navy text-white' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {s.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Status changes are handled via work order actions on the list view
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <button type="button" onClick={onCancel} className="btn btn-secondary" disabled={isLoading}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </>
            ) : isEditing ? 'Update Work Order' : 'Create Work Order'}
          </button>
        </div>
      </form>
    </div>
  )
}
